import { PageSizes } from "../config/pageSizes";
import {
  dayBounds,
  presetRangeValues,
  rangeBounds,
  toDay as toLocalDay,
} from "./dateRangePresets";
import { ListRow } from "./listRows";
import {
  ListFilterDefinition,
  ListFilterValues,
  dateKey,
  fromKey,
  operatorKey,
  presetKey,
  toKey,
} from "./listFilters.types";
import { numberMatches, toNumberFilterOperator } from "./numberFilterOperators";

function setOrDelete(
  params: URLSearchParams,
  param: string,
  value: string | number | null
) {
  if (value === null) {
    params.delete(param);
  } else {
    params.set(param, String(value));
  }
}

/** Every key a filter holds a value under. A date range holds three. */
export function filterKeys(filter: ListFilterDefinition) {
  if (filter.kind === "day") {
    return [dateKey(filter.name), fromKey(filter.name), toKey(filter.name)];
  }

  if (filter.kind === "number") {
    return [filter.name, operatorKey(filter.name)];
  }

  return filter.kind === "date-range"
    ? [presetKey(filter.name), fromKey(filter.name), toKey(filter.name)]
    : [filter.name];
}

/**
 * Every filter empty. A boolean filter starts false rather than absent, and a day
 * filter starts on today, since its search needs a day.
 */
export function emptyFilterValues(
  filters: ListFilterDefinition[]
): ListFilterValues {
  const values: ListFilterValues = {};

  filters.forEach((filter) => {
    if (filter.kind === "boolean") {
      values[filter.name] = filter.defaultValue ?? false;

      return;
    }

    filterKeys(filter).forEach((key) => {
      values[key] = "";
    });

    if (filter.kind === "day") {
      values[dateKey(filter.name)] = toLocalDay(new Date());
    }
  });

  return values;
}

/** Whether anything at all is being filtered on, which is what makes a link worth reading. */
export function hasAnyFilterValue(values: ListFilterValues) {
  return Object.values(values).some((value) =>
    typeof value === "boolean" ? value : Boolean(value)
  );
}

/** Whether one filter is narrowing anything, which is what the Advanced count counts. */
export function hasFilterValue(
  filter: ListFilterDefinition,
  values: ListFilterValues
) {
  if (filter.kind === "boolean") {
    return Boolean(values[filter.name]) !== Boolean(filter.defaultValue);
  }

  // A chosen operator with no number asks nothing of the rows.
  const keys =
    filter.kind === "number" ? [filter.name] : filterKeys(filter);

  return keys.some((key) => {
    const value = values[key];

    return typeof value === "boolean" ? value : Boolean(value);
  });
}

const STATUS_PARAM = "status";

/**
 * Reads filter values out of the query string, so a filtered list is a shareable link.
 * Namespaced by entity, as the legacy tables were, so two lists cannot read each
 * other's parameters.
 */
export function readFilterValuesFromUrl(
  entityName: string,
  filters: ListFilterDefinition[],
  search: string
): ListFilterValues {
  const params = new URLSearchParams(search);
  const values = emptyFilterValues(filters);

  Object.keys(values).forEach((key) => {
    const param = params.get(`${entityName}_${key}`);

    if (param === null) {
      return;
    }

    values[key] = typeof values[key] === "boolean" ? param === "true" : param;
  });

  // A relative range is resolved against today rather than restored as the days it
  // meant when the link was made, which is the whole point of holding the preset.
  filters
    .filter((filter) => filter.kind === "date-range")
    .forEach((filter) => {
      Object.assign(
        values,
        presetRangeValues(
          filter.name,
          String(values[presetKey(filter.name)] ?? ""),
          values
        )
      );
    });

  return values;
}

const PAGE_PARAM = "page";
const PAGE_SIZE_PARAM = "pageSize";
const SORT_PARAM = "sort";
const SORT_DIR_PARAM = "sortDir";

const PAGE_SIZES = PageSizes;

function readNumber(params: URLSearchParams, param: string) {
  const value = Number(params.get(param));

  return Number.isInteger(value) && value > 0 ? value : undefined;
}

/**
 * How the rows are arranged: which page, how many of them, and by which column. Not
 * filters - these survive a reload without describing what is being looked for.
 */
export interface ListViewState {
  page: number;
  pageSize: number;
  sortKey: string;
  sortAscending: boolean;
}

/**
 * What the link says about the arrangement, so a reload comes back to the page that was
 * open, in the order it was in. A page size the control does not offer is ignored; the
 * sort column is the caller's to check against its own columns.
 */
export function readListViewFromUrl(
  entityName: string,
  search: string
): Partial<ListViewState> {
  const params = new URLSearchParams(search);
  const pageSize = readNumber(params, `${entityName}_${PAGE_SIZE_PARAM}`);
  const sortKey = params.get(`${entityName}_${SORT_PARAM}`);
  const sortDir = params.get(`${entityName}_${SORT_DIR_PARAM}`);

  return {
    page: readNumber(params, `${entityName}_${PAGE_PARAM}`),
    pageSize: pageSize && PAGE_SIZES.includes(pageSize) ? pageSize : undefined,
    sortKey: sortKey ?? undefined,
    sortAscending: sortDir === null ? undefined : sortDir !== "desc",
  };
}

/**
 * The arrangement, onto `params`. Each part is set only when it is not the default, so
 * an untouched list has a clean URL, and parameters belonging to other pages are left
 * where they are.
 */
export function applyViewParams(
  params: URLSearchParams,
  entityName: string,
  view: ListViewState,
  defaults: Omit<ListViewState, "page">
) {
  const param = (name: string) => `${entityName}_${name}`;

  setOrDelete(params, param(PAGE_PARAM), view.page > 1 ? view.page : null);
  setOrDelete(
    params,
    param(PAGE_SIZE_PARAM),
    view.pageSize === defaults.pageSize ? null : view.pageSize
  );
  setOrDelete(
    params,
    param(SORT_PARAM),
    view.sortKey === defaults.sortKey ? null : view.sortKey
  );
  setOrDelete(
    params,
    param(SORT_DIR_PARAM),
    view.sortAscending === defaults.sortAscending
      ? null
      : view.sortAscending
        ? "asc"
        : "desc"
  );
}

export function readShowInactiveFromUrl(entityName: string, search: string) {
  return (
    new URLSearchParams(search).get(`${entityName}_${STATUS_PARAM}`) ===
    "inactive"
  );
}

/** The applied filters and the record status, onto `params`. */
export function applyFilterParams(
  params: URLSearchParams,
  entityName: string,
  values: ListFilterValues,
  showInactive: boolean
) {
  Object.entries(values).forEach(([key, value]) => {
    const empty = value === undefined || value === "" || value === false;

    setOrDelete(params, `${entityName}_${key}`, empty ? null : String(value));
  });

  setOrDelete(
    params,
    `${entityName}_${STATUS_PARAM}`,
    showInactive ? "inactive" : null
  );
}

function includesText(row: ListRow, keys: string[], term: string) {
  const wanted = term.toLowerCase();

  return keys.some((key) =>
    String(row[key] ?? "")
      .toLowerCase()
      .includes(wanted)
  );
}

/** The day a value falls on, as `yyyy-MM-dd`, or "" if it is not a date. */
function toDay(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const date = new Date(value as string);

  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

/**
 * Whether a row survives the filter values, for a list filtered in the browser.
 * Comparing days as `yyyy-MM-dd` strings makes both bounds inclusive, which is what a
 * date range without a time reads as.
 */
export function rowMatchesFilters(
  row: ListRow,
  filters: ListFilterDefinition[],
  values: ListFilterValues
) {
  return filters.every((filter) => {
    if (filter.kind === "text") {
      const term = values[filter.name];

      return typeof term !== "string" || term === ""
        ? true
        : includesText(row, filter.searchFields ?? [filter.name], term);
    }

    if (filter.kind === "boolean") {
      const on = values[filter.name] === true;

      if (filter.hidePast) {
        const cell = row[filter.hidePast];
        // No date never expires; `new Date(null)` would read as 1970.
        const date = cell == null ? null : new Date(cell as string);

        return !on || !date || Number.isNaN(date.getTime()) || date >= new Date();
      }

      if (filter.onlyWhenOff) {
        const cell = row[filter.onlyWhenOff.key];
        // An option set can arrive as { Id, Name } rather than the bare value.
        const held =
          typeof cell === "object" && cell !== null
            ? (cell as { Id?: unknown }).Id
            : cell;

        return on || String(held ?? "") === filter.onlyWhenOff.equals;
      }

      // Off means "don't filter", not "match false" - the same as an empty text box.
      return on ? Boolean(row[filter.name]) : true;
    }

    if (filter.kind === "number") {
      const wanted = values[filter.name];

      return typeof wanted !== "string" ||
        wanted === "" ||
        Number.isNaN(Number(wanted))
        ? true
        : numberMatches(
            row[filter.name],
            toNumberFilterOperator(values[operatorKey(filter.name)]),
            Number(wanted)
          );
    }

    if (filter.kind === "lookup" || filter.kind === "picklist") {
      const picked = values[filter.name];

      const cell = row[filter.name];
      // An option set can arrive as { Id, Name } rather than the bare value.
      const held =
        typeof cell === "object" && cell !== null
          ? (cell as { Id?: unknown }).Id
          : cell;

      return typeof picked !== "string" || picked === ""
        ? true
        : String(held ?? "") === picked;
    }

    if (filter.kind === "day") {
      const { day, from, to } = dayBounds(values, filter.name);
      const value = row[filter.name];
      const date = value ? new Date(value as string) : undefined;

      if (!date || Number.isNaN(date.getTime())) {
        return false;
      }

      const time = date.toTimeString().slice(0, 5);
      return toLocalDay(date) === day && time >= from && time <= to;
    }

    // Whatever the range was set to, a preset included, arrives here as two days.
    const { from, to } = rangeBounds(values, filter.name);
    const day = toDay(row[filter.name]);

    if (from !== "" && (day === "" || day < from)) {
      return false;
    }

    return !(to !== "" && (day === "" || day > to));
  });
}
